import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import {
  getSubscriptionPlanEditData,
  updateConfig,
} from "../../../../store/slices/User/SubscriptionPlans/subscriptionPlanSlice";
import {
  useGetSubscriptionPlanBasicDataQuery,
  useGetSubscriptionPlanListDataQuery,
  useUpdateSubscriptionPlanStatusMutation,
  useUpdateSubscriptionPlanTableHeadDataMutation,
} from "../../../../store/queries/Subscription";
import { deleteSubscriptionPlan } from "../api";

/**
 * @param {string} [groupProductId]  when set, the table lists the sizes under one tile rather
 *                                   than the catalogue - the same rows, one level down. The
 *                                   tile's key, which is its group id rather than the product
 *                                   the name suggests; see PlanGroupList.
 */
const usePlanList = (groupProductId) => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const planState = useSelector((state) => state.subscriptionPlan);
  const { showEditModal } = useSelector((state) => state.global);

  const {
    data: planList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetSubscriptionPlanListDataQuery({
    page_size: planState?.currentPageSize,
    page: planState?.currentPage,
    sort: planState?.sortBy,
    sort_order: planState?.sortOrder,
    search: planState?.search,
    group_product_id: groupProductId,
  });

  // Only for the row status picker. The plan has three states rather than two, so the
  // labels have to come from the service rather than being inferred from a checkbox.
  const { data: basicData = {} } = useGetSubscriptionPlanBasicDataQuery();
  const [updateSubscriptionPlanFields] =
    useUpdateSubscriptionPlanTableHeadDataMutation();
  const [updatePlanStatus] = useUpdateSubscriptionPlanStatusMutation();

  const planStatuses = basicData?.data?.plan_statuses ?? [];

  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = !state.showCreateModal;
        state.isEdit = false;
        state.editPlanData = {};
        state.imageName = "";
        state.imagePreview = "";
        state.aliasImageName = "";
        state.aliasImagePreview = "";
        state.pricing = {};
      })
    );
  };

  useEffect(() => {
    if (planState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [planState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  /**
   * Publishing re-prices the plan on the way through and can be refused - a plan whose
   * discount rounds an item to zero cannot go on sale. So this waits for the answer and
   * refetches rather than moving the row optimistically, which would tell the team a plan
   * went live when it had been turned down.
   */
  const handleStatusChange = async (planId, status) => {
    const response = await updatePlanStatus({
      plan_id: planId,
      status: Number(status),
    });

    if (response?.data?.status_code === 200) {
      toast.success(`Plan status updated successfully`);
      refetch();
    } else {
      toast.error(
        response?.error?.data?.message ??
          response?.data?.message ??
          `Unable to update the plan status`
      );
    }
  };

  const handleOpenGroup = (data) => {
    // The page the tile opens starts at its own first page, not wherever the catalogue was.
    dispatch(
      updateConfig((state) => {
        state.currentPage = 1;
        state.search = "";
      })
    );
    navigate(`/user/subscription-plans/group/${data?.group_product_id}`);
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      // A tile stands for several plans, so its name opens them rather than an edit form for
      // one of them. Every other row keeps reading as plain text.
      name: (field, data) =>
        data?.is_group_row ? (
          <button
            className="pro-btn pro-btn-link pro-p-0"
            onClick={() => handleOpenGroup(data)}
          >
            <span className="pro-fw-medium">{data?.name}</span>
            <span className="pro-ms-1 pro-font-xs">
              ({data?.group_size_count} sizes)
            </span>
          </button>
        ) : (
          <p className="pro-mb-0">{data?.name}</p>
        ),
      status: (field, data) => {
        const current = planStatuses?.find(
          (item) => item?.name === data?.status
        );
        return (
          <select
            className="pro-input lg"
            value={current?.id ?? ""}
            disabled={!planList?.edit_permission}
            onChange={(e) => handleStatusChange(data?.id, e.target.value)}
          >
            {planStatuses?.map((item) => (
              <option key={item?.id} value={item?.id}>
                {item?.name}
              </option>
            ))}
          </select>
        );
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleSort = (label) => {
    if (planState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = planState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
        state.selectedId = e?.[0];
        state.isEdit = true;
        state.pricing = {};
      })
    );
    dispatch(getSubscriptionPlanEditData(e?.[0]));
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedId = e?.[0];
      })
    );
  };

  const handleDelete = async () => {
    const response = await deleteSubscriptionPlan(planState?.selectedId);
    try {
      if (response?.data?.status_code === 200) {
        toast.success(`Plan deleted successfully`);
        refetch();
        dispatch(
          updateConfig((state) => {
            state.clearSelection = true;
          })
        );
      } else {
        toast.error(
          response?.data?.message ?? `Failed to delete the plan. Please try again.`
        );
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      handleDeleteModal();
    }
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  return {
    isLoading,
    isFetching,
    planList,
    planState,
    showEditModal,
    paginationOptions,
    handleEditColumnsClick,
    updateSubscriptionPlanFields,
    handleDeleteModal,
    handleCreateClick,
    handlePagination,
    handleClearClick,
    handleEditAction,
    handlePageSize,
    handleDelete,
    handleSearch,
    handleSort,
    handleOpenGroup,
    refetch,
    getRow,
  };
};

export default usePlanList;
